import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, DownloadSimple, MagnifyingGlass, Trash } from "@phosphor-icons/react";

const FLAG = { rosso: "bg-rose-500", giallo: "bg-amber-500", verde: "bg-emerald-500", grigio: "bg-slate-400" };
const PPE_FORM = [["code", "Codice DPI"], ["name", "Nome DPI"], ["brand", "Marca"], ["model", "Modello"],
  ["size", "Taglia"], ["quantity", "Quantità"], ["unit", "Unità"], ["purchase_date", "Data acquisto"],
  ["expiry_date", "Data scadenza"], ["duration_months", "Durata (mesi)"], ["last_check", "Ultima verifica"],
  ["next_check", "Prossima verifica"], ["description", "Descrizione"], ["notes", "Note"]];
const EQ_FORM = [["code", "Codice"], ["name", "Nome attrezzatura"], ["brand", "Marca"], ["model", "Modello"],
  ["serial", "Matricola"], ["inventory_number", "N. inventario"], ["department", "Reparto"], ["area", "Area"],
  ["purchase_date", "Data acquisto"], ["service_date", "Entrata in servizio"], ["responsible", "Responsabile"],
  ["last_maintenance", "Ultima manutenzione"], ["next_maintenance", "Prossima manutenzione"],
  ["last_check", "Ultima verifica"], ["next_check", "Prossima verifica"], ["expiry_date", "Scadenza"],
  ["maintenance_frequency", "Frequenza manutenzione"], ["check_frequency", "Frequenza verifica"], ["notes", "Note"]];

export default function Safety({ tab = "dashboard" }) {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [form, setForm] = useState(null);
  const [ppeList, setPpeList] = useState([]);

  useEffect(() => {
    api.get("/safety/ppe").then((r) => setPpeList(r.data)).catch(() => {});
  }, [tab]);

  useEffect(() => {
    api.get("/safety/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      if (tab === "dashboard") setData((await api.get("/safety/dashboard")).data);
      else if (tab === "ppe") setRows((await api.get("/safety/ppe", { params: { q, state } })).data);
      else if (tab === "assignments") setRows((await api.get("/safety/assignments")).data);
      else if (tab === "equipment") setRows((await api.get("/safety/equipment", { params: { q, state } })).data);
      else if (tab === "log") setRows((await api.get("/safety/activities", { params: { q } })).data);
      else if (tab === "anomalies") setRows((await api.get("/safety/anomalies", { params: { q, state } })).data);
      else if (tab === "deadlines") setData((await api.get("/safety/deadlines")).data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [tab, q, state]);

  useEffect(() => { load(); }, [load]);

  const save = async (path, body, method = "post") => {
    try {
      await api[method](path, body);
      toast.success("Salvato");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removePpe = async (r) => {
    if (!window.confirm(`Eliminare il DPI ${r.code} — ${r.name}?\n\nLe consegne e le firme già registrate restano nello storico.`)) return;
    try {
      const res = await api.delete(`/safety/ppe/${r.id}`);
      toast.success(res.data?.message || "DPI eliminato");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Toolbar = ({ children }) => (
    <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ricerca libera..."
          className="h-9 w-60 pl-8 rounded-sm" data-testid="safety-search" />
      </div>
      {children}
    </div>
  );

  const Table = ({ cols, items, onRow, actions }) => (
    <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
      <table className="w-full text-sm tbl-sticky" data-testid="safety-table">
        <thead className="bg-secondary"><tr>
          {actions && <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Azioni</th>}
          {cols.map(([, l]) => <th key={l} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{l}</th>)}
        </tr></thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id || i} className="border-t border-border hover:bg-secondary/40"
              onClick={() => onRow && onRow(r)} data-testid={`safety-row-${r.code || r.id || i}`}>
              {actions && (
                <td className="px-3 py-1.5 text-left" onClick={(e) => e.stopPropagation()}>
                  {actions(r)}
                </td>
              )}
              {cols.map(([k]) => (
                <td key={k} className="px-3 py-1.5 whitespace-nowrap">
                  {k === "flag"
                    ? <span className={`inline-block h-3 w-3 rounded-full ${FLAG[r[k]] || "bg-slate-400"}`} />
                    : /date$|^date|_at$/.test(k) ? (itDate(r[k]) || "—")
                      : Array.isArray(r[k]) ? r[k].join(", ") : (r[k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={cols.length + (actions ? 1 : 0)} className="px-3 py-8 text-center text-muted-foreground" data-testid="safety-empty">Nessun dato</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const Exports = ({ report }) => (
    <div className="flex gap-2">
      {["pdf", "xlsx", "csv"].map((f) => (
        <Button key={f} variant="outline" size="sm" className="rounded-sm" data-testid={`safety-export-${f}`}
          onClick={() => download(`/safety/export/${report}?fmt=${f}`, `${report}.${f}`).catch((e) => toast.error(errMsg(e)))}>
          <DownloadSimple size={14} className="mr-1.5" /> {f === "xlsx" ? "EXCEL" : f.toUpperCase()}
        </Button>
      ))}
    </div>
  );

  const Kpi = ({ label, value }) => (
    <div className="rounded-sm border border-border bg-card p-3" data-testid={`kpi-${label}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-head font-bold tabular-nums">{value ?? "—"}</div>
    </div>
  );

  // funzione (non componente) per evitare il rimontaggio del dialog a ogni tasto premuto
  const formDialog = ({ fields, title, path }) => (
    <Dialog open onOpenChange={() => setForm(null)}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="safety-dialog">
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map(([k, l]) => (
            <div key={k}>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
              <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="h-9 rounded-sm mt-1" data-testid={`sf-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Categoria</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="sf-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {(tab === "equipment" ? meta?.equipment_categories : meta?.ppe_categories)?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Stato</Label>
            <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
              <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="sf-state"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(tab === "equipment" ? meta?.equipment_states : meta?.ppe_states)?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="safety-cancel">Annulla</Button>
          <Button className="rounded-sm font-semibold" data-testid="safety-save"
            onClick={() => save(form.id ? `${path}/${form.id}` : path,
              { ...form, quantity: Number(form.quantity || 0), duration_months: form.duration_months ? Number(form.duration_months) : null },
              form.id ? "put" : "post")}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const TITLES = { dashboard: "Dashboard Sicurezza", ppe: "DPI", assignments: "Consegne e firme DPI",
    equipment: "Attrezzature", log: "Registro Attività Preposto", anomalies: "Controlli e anomalie",
    deadlines: "Scadenze" };

  return (
    <div className="space-y-3" data-testid={`safety-${tab}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">{TITLES[tab]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Modulo Sicurezza e Preposto — collegato all'anagrafica dipendenti</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {tab === "ppe" && <Exports report="dpi" />}
          {tab === "assignments" && <Exports report="dpi-assegnati" />}
          {tab === "equipment" && <Exports report="attrezzature" />}
          {tab === "log" && <Exports report="registro-preposto" />}
          {tab === "anomalies" && <Exports report="anomalie" />}
          {tab === "deadlines" && <Exports report="scadenze" />}
          {canEdit && tab === "ppe" && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="safety-add"
              onClick={() => setForm({ category: "Guanti", state: "Disponibile", unit: "pz", quantity: 1 })}>
              <Plus size={15} className="mr-1.5" /> Nuovo DPI
            </Button>
          )}
          {canEdit && tab === "equipment" && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="safety-add"
              onClick={() => setForm({ category: "Coltelli", state: "Operativa" })}>
              <Plus size={15} className="mr-1.5" /> Nuova attrezzatura
            </Button>
          )}
          {canEdit && tab === "assignments" && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="safety-add"
              onClick={() => setForm({ kind: "assign", quantity: 1 })}>
              <Plus size={15} className="mr-1.5" /> Nuova consegna
            </Button>
          )}
          {canEdit && tab === "log" && (
            <Button size="sm" className="rounded-sm font-semibold" data-testid="safety-add"
              onClick={() => setForm({ kind: "activity", activity_type: "Controllo reparto", result: "Positivo" })}>
              <Plus size={15} className="mr-1.5" /> NUOVA ATTIVITÀ
            </Button>
          )}
        </div>
      </div>

      {tab === "dashboard" && data && (
        <>
          <h2 className="font-head font-semibold">DPI</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Kpi label="Totali" value={data.ppe.total} /><Kpi label="Assegnati" value={data.ppe.assigned} />
            <Kpi label="Disponibili" value={data.ppe.available} /><Kpi label="In scadenza" value={data.ppe.expiring} />
            <Kpi label="Scaduti" value={data.ppe.expired} /><Kpi label="Da sostituire" value={data.ppe.to_replace} />
          </div>
          <h2 className="font-head font-semibold pt-2">Attrezzature</h2>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            <Kpi label="Totali" value={data.equipment.total} /><Kpi label="Operative" value={data.equipment.operational} />
            <Kpi label="In manutenzione" value={data.equipment.maintenance} /><Kpi label="Fuori servizio" value={data.equipment.out_of_service} />
            <Kpi label="Da verificare" value={data.equipment.to_check} /><Kpi label="Verifiche in scadenza" value={data.equipment.check_expiring} />
            <Kpi label="Manut. in scadenza" value={data.equipment.maintenance_expiring} />
          </div>
          <h2 className="font-head font-semibold pt-2">Preposto</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Attività oggi" value={data.supervisor.today} /><Kpi label="Attività mese" value={data.supervisor.month} />
            <Kpi label="Anomalie aperte" value={data.supervisor.open_anomalies} />
            <Kpi label="Anomalie scadute" value={data.supervisor.overdue_anomalies} />
            <Kpi label="Esiti negativi" value={data.supervisor.negative} />
          </div>
        </>
      )}

      {tab === "deadlines" && data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            {["rosso", "giallo", "verde", "grigio"].map((k) => (
              <div key={k} className="rounded-sm border border-border bg-card p-3" data-testid={`flag-${k}`}>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span className={`h-3 w-3 rounded-full ${FLAG[k]}`} /> {k}
                </div>
                <div className="mt-1 text-2xl font-head font-bold tabular-nums">{data.counts[k]}</div>
              </div>
            ))}
          </div>
          <Table cols={[["flag", ""], ["type", "Tipo"], ["subject", "Oggetto"], ["reference", "Riferimento"], ["due_date", "Scadenza"], ["extra", "Stato"]]} items={data.rows} />
        </>
      )}

      {["ppe", "equipment", "assignments", "log", "anomalies"].includes(tab) && (
        <>
          <Toolbar>
            {["ppe", "equipment", "anomalies"].includes(tab) && (
              <Select value={state} onValueChange={setState}>
                <SelectTrigger className="h-9 w-[190px] rounded-sm" data-testid="safety-state-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {(tab === "equipment" ? meta?.equipment_states : tab === "anomalies" ? meta?.anomaly_states : meta?.ppe_states)?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </Toolbar>
          {tab === "ppe" && <Table cols={[["code", "Codice"], ["name", "DPI"], ["category", "Categoria"], ["brand", "Marca"], ["size", "Taglia"], ["quantity", "Q.tà"], ["state", "Stato"], ["expiry_date", "Scadenza"], ["next_check", "Prossima verifica"]]}
            items={rows} onRow={(r) => canEdit && setForm({ ...r })}
            actions={canEdit ? (r) => (
              <div className="flex items-center justify-start gap-3 whitespace-nowrap">
                <button type="button" className="text-xs font-semibold hover:underline px-1 py-1" data-testid={`ppe-edit-${r.code}`}
                  onClick={() => setForm({ ...r })}>MODIFICA</button>
                <button type="button" className="text-rose-600 hover:text-rose-700 p-1" title="Elimina DPI"
                  data-testid={`ppe-delete-${r.code}`} onClick={() => removePpe(r)}>
                  <Trash size={16} />
                </button>
              </div>
            ) : null} />}
          {tab === "equipment" && <Table cols={[["code", "Codice"], ["name", "Attrezzatura"], ["category", "Categoria"], ["serial", "Matricola"], ["department", "Reparto"], ["state", "Stato"], ["next_maintenance", "Prossima manut."], ["next_check", "Prossima verifica"]]}
            items={rows} onRow={(r) => canEdit && setForm({ ...r })} />}
          {tab === "assignments" && <Table cols={[["employee_name", "Dipendente"], ["ppe_code", "Codice DPI"], ["ppe_name", "DPI"], ["quantity", "Q.tà"], ["size", "Taglia"], ["delivery_date", "Consegna"], ["return_date", "Restituzione"], ["state", "Stato"], ["signature", "Firma consegna"]]} items={rows} />}
          {tab === "log" && <Table cols={[["date", "Data"], ["time", "Ora"], ["supervisor", "Preposto"], ["department", "Reparto"], ["activity_type", "Tipo attività"], ["result", "Esito"], ["anomaly", "Anomalia"], ["state", "Stato"]]} items={rows} />}
          {tab === "anomalies" && <Table cols={[["date", "Data"], ["description", "Anomalia"], ["severity", "Gravità"], ["department", "Reparto"], ["action_owner", "Responsabile"], ["due_date", "Scadenza"], ["state", "Stato"]]} items={rows} />}
        </>
      )}

      {form && form.kind === "assign" && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="safety-dialog">
            <DialogHeader><DialogTitle className="font-head">Consegna DPI</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="sf-employee"><SelectValue placeholder="Dipendente" /></SelectTrigger>
                <SelectContent className="max-h-64">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.ppe_id} onValueChange={(v) => setForm({ ...form, ppe_id: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="sf-ppe"><SelectValue placeholder="DPI" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {ppeList.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {[["quantity", "Quantità"], ["size", "Taglia"], ["delivery_date", "Data consegna"], ["replacement_due", "Sostituzione prevista"], ["signature", "Firma/conferma consegna"], ["notes", "Note"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
                  <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 rounded-sm mt-1" data-testid={`sf-${k}`} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button className="rounded-sm font-semibold" data-testid="safety-save"
                onClick={() => save("/safety/assignments", { ...form, quantity: Number(form.quantity || 1) })}>Registra consegna</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {form && form.kind === "activity" && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="safety-dialog">
            <DialogHeader><DialogTitle className="font-head">Nuova attività preposto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="sf-activity_type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{meta?.activity_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {[["department", "Reparto"], ["area", "Area"], ["description", "Descrizione"], ["result", "Esito"],
                ["anomaly", "Anomalia rilevata (facoltativa)"], ["severity", "Gravità"], ["immediate_action", "Azione immediata"],
                ["corrective_action", "Azione correttiva"], ["action_owner", "Responsabile azione"], ["due_date", "Scadenza"], ["notes", "Note"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</Label>
                  <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 rounded-sm mt-1" data-testid={`sf-${k}`} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button className="rounded-sm font-semibold" data-testid="safety-save"
                onClick={() => save("/safety/activities", { ...form, employee_ids: [] })}>SALVA REGISTRAZIONE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {form && !form.kind && tab === "ppe" && formDialog({ fields: PPE_FORM, title: form.id ? "Modifica DPI" : "Nuovo DPI", path: "/safety/ppe" })}
      {form && !form.kind && tab === "equipment" && formDialog({ fields: EQ_FORM, title: form.id ? "Modifica attrezzatura" : "Nuova attrezzatura", path: "/safety/equipment" })}
    </div>
  );
}
